import {
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFrame, useThree } from "@react-three/fiber";
import {
  Float,
  Text,
  useAnimations,
  useCursor,
  useGLTF,
} from "@react-three/drei";
import { SkeletonUtils } from "three-stdlib";
import * as THREE from "three";
import { selectionContext } from "@react-three/postprocessing";
import Placeholder from "../Placeholder";
import useCompileReady from "../useCompileReady";
import { useProjectFocus } from "@/stores/projectFocusStore";
import { computeFocusFrame } from "../focusUtils";
import useFocusOcclusionHidden from "../useFocusOcclusionHidden";

// Like drei's <Select>, but matches on isMesh instead of type === "Mesh" so
// skinned meshes (type "SkinnedMesh", e.g. the Cria llama) get outlined too.
function SelectMeshes({ enabled, children }) {
  const group = useRef();
  const api = useContext(selectionContext);
  useEffect(() => {
    if (!api || !enabled) return;
    const current = [];
    group.current.traverse((o) => {
      if (o.isMesh) current.push(o);
    });
    api.select((state) => [...state, ...current]);
    return () => {
      api.select((state) => state.filter((s) => !current.includes(s)));
    };
  }, [enabled, api]);
  return <group ref={group}>{children}</group>;
}

function ProjectModelInner({
  modelPath,
  name,
  src,
  description = "",
  // Focus-view azimuth for this model, radians around the vertical axis
  // (negative = clockwise seen from above). 0 = straight front view.
  focusYaw = 0,
  // Multiplier on the auto-fit focus zoom. >1 zooms in closer; use to fix
  // models whose bounding box is inflated by stray geometry in the GLB.
  focusZoom = 1,
  modelOffset = [0, 0, 0],
  modelScale = 1,
  modelRotation = [0, 0, 0],
  labelOffset = [0, -2, 0],
  labelSize = 1,
  rotationIntensity = 0,
  floatIntensity = 0,
  animationSpeed = 1,
  pingPongClips = [],
  // Gentle idle sway around the model origin: roll tilts top left/right,
  // yaw adds a slight turn. Radians; speed in cycles-ish per second.
  sway = null,
  ...floatProps
}) {
  const groupRef = useRef();
  const { size } = useThree();
  const { scene, animations } = useGLTF(modelPath);
  const clonedScene = useMemo(() => {
    const clone = SkeletonUtils.clone(scene);
    clone.traverse((child) => {
      if (child.isMesh) child.castShadow = true;
    });
    return clone;
  }, [scene]);
  const { actions, names } = useAnimations(animations, clonedScene);
  const [hovered, setHovered] = useState(false);
  useCursor(hovered);
  const focusId = `project:${name}`;
  // Hide this model while it blocks the view of another focused model.
  const occluded = useFocusOcclusionHidden(focusId, groupRef);
  // Stay hidden (placeholder up) until shaders are compiled off-thread, so
  // the first draw never stutters the visible scene.
  const compiled = useCompileReady(clonedScene);

  useFrame(({ clock }) => {
    if (!sway || !groupRef.current) return;
    const t = clock.elapsedTime * (sway.speed ?? 1);
    groupRef.current.rotation.z = Math.sin(t) * (sway.roll ?? 0);
    groupRef.current.rotation.y = Math.sin(t * 0.7) * (sway.yaw ?? 0);
    // Pitch rocks from rest up to -pitch (looking up) and back.
    groupRef.current.rotation.x =
      -(((Math.sin(t) + 1) / 2) * (sway.pitch ?? 0));
  });

  useEffect(() => {
    names.forEach((n) => {
      const action = actions[n];
      if (!action) return;
      if (pingPongClips.includes(n)) {
        action.setLoop(THREE.LoopPingPong, Infinity);
      }
      action.reset().fadeIn(0.3).play();
      action.timeScale = animationSpeed;
    });
    return () => {
      names.forEach((n) => actions[n]?.fadeOut(0.3).stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actions, names, animationSpeed, pingPongClips.join("|")]);

  return (
    <Float
      rotationIntensity={rotationIntensity}
      floatIntensity={floatIntensity}
      {...floatProps}
    >
      {!compiled && <Placeholder position={modelOffset} scale={[2, 2, 2]} />}
      <group
        ref={groupRef}
        visible={compiled && !occluded}
        position={modelOffset}
        scale={modelScale}
        rotation={modelRotation}
        onClick={(e) => {
          e.stopPropagation();
          const { focused, setFocus } = useProjectFocus.getState();
          const openSite = () => {
            if (src) window.open(src, "_blank", "noopener,noreferrer");
          };
          // First click focuses the camera; clicking the already-focused
          // model opens its site (same as the card's action button).
          if (focused?.id === focusId) {
            openSite();
            return;
          }
          const frame = computeFocusFrame(groupRef.current, size);
          setFocus({
            id: focusId,
            name,
            description,
            ...frame,
            zoom: frame.zoom * focusZoom,
            yaw: focusYaw,
            actionLabel: "Visit site",
            onAction: openSite,
          });
        }}
        // Stop pointerdown/up so GroundPlane's click-to-move (which listens
        // on those, not onClick) never fires for clicks on a project.
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
        onPointerOver={(e) => {
          e.stopPropagation();
          setHovered(true);
        }}
        onPointerOut={() => setHovered(false)}
      >
        <SelectMeshes enabled={hovered}>
          <primitive object={clonedScene} />
        </SelectMeshes>
      </group>
      {name && (
        <Text
          font="fonts/Inter-Regular.woff"
          fontSize={labelSize}
          color="white"
          position={labelOffset}
          anchorY="top"
        >
          {name}
        </Text>
      )}
    </Float>
  );
}

export default function ProjectModel(props) {
  return (
    <Suspense
      fallback={
        <Placeholder
          position={props.position || [0, 0.5, 0]}
          scale={[2, 2, 2]}
        />
      }
    >
      <ProjectModelInner {...props} />
    </Suspense>
  );
}

ProjectModel.preload = (modelPath) => useGLTF.preload(modelPath);
